import { Inject } from '@odinjs/odin';
import { ActionAPI, AutocompleteAPI, BreadcrumbAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, ModalService, RadioGroupAPI, SelectableItem } from '@philips-internal/oswald-ui-components';
import ModalButtonType from '@philips-internal/oswald-ui-constants/lib/modal-button-type';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnCancel, OnChange, OnClick, OnEdit, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, HttpStatus, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, MyChartAllergieDetailAliases, MyChartAllergiesAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';

interface AllergieDetail extends Record<string, Value>{
  allergieDescription: string;
  allergieNotRegistered : string;
  allergieObj: Autocomplete;
  allergieSequence: number;
  classification: string;
  classificationObj: SelectableItem;
  disableDate?: string;
  endDate?: string;
  id: string;
  intensity: string;
  intensityObj: SelectableItem;
  isNew?: boolean;
  notes: string;
  reactionDescription: string;
  reactionNotRegistered : string;
  reactionObj: Autocomplete;
  reactionSequence: number;
  sequence: string;
  startDate?: string;
  submitDate?: string;
  typeReaction: string;

}

interface Autocomplete extends Record<string, Value>{
  description: string;
  sequence: number;
}

enum ValidatorsInUse {
  REQUIRED = 'required',
}

enum intensity {
  light = 'L',
  moderate = 'M',
  intense = 'I',
  unknown = 'D'
}

enum category {
  allergie = 'A',
  intolerance = 'I'
}

@Controller({ fragment: FragmentNames.MY_CHART_ALLERGIE_DETAIL })
export default class MyChartAllergieDetailController extends BaseController {

  @Inject(MyChartAllergieDetailAliases.FORM_ALLERGIE)
    allergieFormAPI!: FormAPI;

  @Inject(MyChartAllergieDetailAliases.FETCH_REACTION_TYPE)
    reactionTypeFetchAPI!: FetchAPI;

  @Inject(MyChartAllergieDetailAliases.AUTOCOMPLETE_ALLERGIE)
    autocompleteAllergieTypeAPI!: AutocompleteAPI;

  @Inject(MyChartAllergieDetailAliases.AUTOCOMPLETE_REACTION_TYPE)
    autocompleteReactionTypeAPI!: AutocompleteAPI;

  @Inject(MyChartAllergieDetailAliases.FIELD_ALLERGIE)
    fieldAllergieAPI!: FieldAPI;

  @Inject(MyChartAllergieDetailAliases.FIELD_ALLERGIE_NOT_REGISTERED)
    fieldAllergieNotRegisteredAPI!: FieldAPI;

  @Inject(MyChartAllergieDetailAliases.INPUT_ALLERGIE_NOT_REGISTERED)
    inputAllergieNotRegisteredAPI!: InputAPI;

  @Inject(MyChartAllergieDetailAliases.FIELD_REACTION_TYPE)
    fieldReactionTypeAPI!: FieldAPI;

  @Inject(MyChartAllergieDetailAliases.FIELD_REACTION_NOT_REGISTERED)
    fieldReactionNotRegisteredAPI!: FieldAPI;

  @Inject(MyChartAllergieDetailAliases.INPUT_REACTION_NOT_REGISTERED)
    inputReactionNotRegisteredAPI!: InputAPI;

  @Inject(MyChartAllergieDetailAliases.ACTION_DELETE)
    actionDeleteAPI!: ActionAPI;

  @Inject(MyChartAllergieDetailAliases.ACTION_SUBMIT)
    actionSubmitAPI!: ActionAPI;

  @Inject(MyChartAllergieDetailAliases.BREADCRUMB_ALLERGIES)
    breadcrumbAllergiesAPI!: BreadcrumbAPI;

  @OnMount(MyChartAllergieDetailAliases.FORM_ALLERGIE)
  onFormMount(event: MountEvent): void {
    const allergieFormApi = event.target as FormAPI;
    const { nrSequence, tab } = this.historyService.getCurrentRouteParams();
    allergieFormApi.setRecord({});

    if (!nrSequence){
      this.handleDisplayButtons(false, false);
      allergieFormApi.setDisabled(false);
      allergieFormApi.setEditing(true);
      return;
    }

    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_ALLERGIE_DETAIL, { nrSequence: nrSequence, patientId: UserContext.patientId, tab: tab})
      .then(response => response.body as AllergieDetail)
      .then(data => {
        const detail = data;
        const isPatientTab = tab === MyChartAllergiesAliases.TAB_PATIENT;

        const showDelete = isPatientTab && !detail.disableDate;
        const showSubmit = isPatientTab && !detail.submitDate;
        this.handleDisplayButtons(showDelete, showSubmit);
        allergieFormApi.setDisabled(!showSubmit);

        detail.sequence = nrSequence.toString();
        detail.allergieObj = {
          description: data.allergieDescription,
          sequence: data.allergieSequence
        };

        detail.reactionObj = {
          description: data.reactionDescription,
          sequence: data.reactionSequence
        };

        if (showSubmit){
          this.actionDeleteAPI.setText(MessageService.translate('tws.form.delete'));
        }

        if (detail.classification === category.intolerance) {
          detail.classificationObj = { key: '1', label: MessageService.translate('patient.my.chart.intolerance'), value: category.intolerance };
        } else {
          detail.classificationObj = { key: '2', label: MessageService.translate('patient.label.allergies'), value: category.allergie };
        }

        switch (detail.intensity) {
          case intensity.light:
            detail.intensityObj = { key:'1', label: MessageService.translate('patient.my.chart.intolerance.light'), value: intensity.light }
            break;
          case intensity.moderate:
            detail.intensityObj = { key:'2', label: MessageService.translate('patient.my.chart.intolerance.moderate'), value: intensity.moderate }
            break;
          case intensity.intense:
            detail.intensityObj = { key:'3', label: MessageService.translate('patient.my.chart.intolerance.intense'), value: intensity.intense }
            break;
          case intensity.unknown:
            detail.intensityObj = { key:'4', label: MessageService.translate('patient.my.chart.intolerance.unknown'), value: intensity.unknown }
            break;
        }
        allergieFormApi.setData(detail);
      });
  }

  @OnCancel(MyChartAllergieDetailAliases.FORM_ALLERGIE)
  onClickCancelButton(): void {
    this.allergieFormAPI.setDisabled(false);
    this.allergieFormAPI.setEditing(false);

    if (this.historyService.getCurrentRoute().includes('/new')) {
      this.popNavigationToAllergy();
    } else {
      this.handleDisplayButtons(true, true);
    }
  }

  releaseAllergie(allergieId : string): void {
    HttpService.put(
      APPLICATION_ENDPOINTS.PUT_RELEASE_MC_ALLERGIES, { allergieId: allergieId }
    ).then(response => {
      if (response.status === HttpStatus.OK) {
        showToastNotificationSuccess(MessageService.translate('patient.my-chart.released-success'));
        this.handleDisplayButtons(true, false);
        this.allergieFormAPI.setDisabled(true);
        this.actionDeleteAPI.setText(MessageService.translate('patient.label.inactive'));
      }

      if (this.historyService.getCurrentRoute().includes('/new')) {
        this.popNavigationToAllergy();
      }
    });
  }

  @OnSubmit(MyChartAllergieDetailAliases.FORM_ALLERGIE)
  OnSubmitAllergie(event: SubmitEvent): void {
    const allergie = event.record as unknown as AllergieDetail;

    this.validateDates(allergie);

    if (allergie.allergieObj) {
      allergie.allergieSequence = allergie.allergieObj.sequence;
    }
    if (allergie.reactionObj) {
      allergie.reactionSequence = allergie.reactionObj.sequence;
    }

    if (allergie.classificationObj) {
      allergie.classification = allergie.classificationObj.value;
    }
    if (allergie.intensityObj) {
      allergie.intensity = allergie.intensityObj.value;
    }

    const body = allergie as unknown as Record<string, Value>
    HttpService.post( APPLICATION_ENDPOINTS.POST_MC_ALLERGIES, { patientId: UserContext.patientId }, { body } ).then(response => {
      if(response.status !== HttpStatus.OK) {
        return;
      }
      const newAllergie = response.body as unknown as AllergieDetail;
      this.afterSaveVisibility();

      if (!allergie.sequence) {
        ModalService.openDialog({
          closeable: true,
          dialogDescription: 'patient.my-chart.modal.want-submit',
          dockButtons: [
            {
              action: (): void => {
                close();
              },
              title: MessageService.translate('patient.modal.button-cancel'),
              type: ModalButtonType.CANCEL
            },
            {
              action: (): void => { this.releaseAllergie(newAllergie.sequence) },
              title: MessageService.translate('patient.moda.button-yes'),
              type: ModalButtonType.SUCCESS
            }
          ]
        });
      } else {
        showToastNotificationSuccess(MessageService.translate('patient.my-chart.changed-success'));
      }

      this.popNavigationToAllergy();
      allergie.sequence = newAllergie.sequence;
      this.allergieFormAPI.setData(allergie);
    });
  }

  @OnChange({ target: MyChartAllergieDetailAliases.AUTOCOMPLETE_ALLERGIE })
  async onChangeAutocompleteAllergieType( event: ChangeEvent<Autocomplete> ): Promise<void> {
    const allergieType = event.value as Autocomplete;
    let params = {};
    await this.onChangeFieldSwap(this.fieldAllergieAPI, this.fieldAllergieNotRegisteredAPI, !allergieType,
      this.inputAllergieNotRegisteredAPI, this.autocompleteAllergieTypeAPI);
    if (allergieType) {
      params = { nrSequence : allergieType.sequence };
    }
    this.reactionTypeFetchAPI.setParameters(params);
    this.reactionTypeFetchAPI.forceFetch();
  }

  @OnChange({ target: MyChartAllergieDetailAliases.AUTOCOMPLETE_REACTION_TYPE })
  async onChangeAutocompleteReactionType( event: ChangeEvent<Autocomplete> ): Promise<void> {
    const reactionType = event.value as Autocomplete;
    await this.onChangeFieldSwap(this.fieldReactionTypeAPI, this.fieldReactionNotRegisteredAPI, !reactionType, this.inputReactionNotRegisteredAPI);
  }

  @OnChange({ target: MyChartAllergieDetailAliases.FIELD_REACTION_NOT_REGISTERED })
  async onChangeReactionNotRegistered( event: ChangeEvent<string> ): Promise<void> {
    const notRegisteredReaction = event.value;
    await this.onChangeFieldSwap(this.fieldReactionNotRegisteredAPI, this.fieldReactionTypeAPI, !notRegisteredReaction,
      this.autocompleteReactionTypeAPI, this.inputReactionNotRegisteredAPI);
  }

  @OnChange({ target: MyChartAllergieDetailAliases.FIELD_ALLERGIE_NOT_REGISTERED })
  async onChangeAllergieNotRegistered( event: ChangeEvent<string> ): Promise<void> {
    const notRegisteredAllergie = event.value;
    await this.onChangeFieldSwap(this.fieldAllergieNotRegisteredAPI, this.fieldAllergieAPI, !notRegisteredAllergie,
      this.autocompleteAllergieTypeAPI, this.inputAllergieNotRegisteredAPI);
  }

  @OnMount({target: MyChartAllergieDetailAliases.RADIO_CATEGORY})
  onMountRadioCategory( evt: MountEvent ): void {
    const radioCategory = evt.target as RadioGroupAPI;
    radioCategory.setData([
      { key:'2', label: MessageService.translate('patient.label.allergies'), value: category.allergie },
      { key:'1', label: MessageService.translate('patient.my.chart.intolerance'), value: category.intolerance }
    ]);
  }

  @OnMount({target: MyChartAllergieDetailAliases.RADIO_INTENSITY})
  onMountRadioItensity(evt: MountEvent ): void {
    const radioIntensity = evt.target as RadioGroupAPI;

    radioIntensity.setData([
      { key:'1', label: MessageService.translate('patient.my.chart.intolerance.light'), value: intensity.light },
      { key:'2', label: MessageService.translate('patient.my.chart.intolerance.moderate'), value: intensity.moderate },
      { key:'3', label: MessageService.translate('patient.my.chart.intolerance.intense'), value: intensity.intense },
      { key:'4', label: MessageService.translate('patient.my.chart.intolerance.unknown'), value: intensity.unknown }
    ]);
  }

  @OnEdit({target: MyChartAllergieDetailAliases.FORM_ALLERGIE})
  onEditForm(): void {
    this.handleDisplayButtons(false, false);
  }

  @OnClick({ target: MyChartAllergieDetailAliases.ACTION_DELETE })
  onDelete(): void {
    const body = this.allergieFormAPI.getData() as AllergieDetail;
    if (this.actionDeleteAPI.getText() === MessageService.translate('tws.form.delete')) {
      HttpService.delete(
        APPLICATION_ENDPOINTS.DELETE_MC_ALLERGIES, { allergieId: body.sequence }
      ).then(response => {
        if (response.status === HttpStatus.OK) {
          showToastNotificationSuccess('patient.my-chart.delete-success');
          this.popNavigationToAllergy();
        }
      });
    } else {
      const context = { endpoint : APPLICATION_ENDPOINTS.PUT_DISABLE_MC_ALLERGIES, sequence : body.sequence };
      ModalService.openDialog({
        closeable: true,
        contextData: context,
        fragment: 'justify-modal',
        id: 'justify-modal'
      });
      this.handleDisplayButtons(true, false, false, true);
      this.allergieFormAPI.setDisabled(true);
    }
  }

  @OnClick({ target: MyChartAllergieDetailAliases.ACTION_SUBMIT })
  onSubmmit(): void {
    const body = this.allergieFormAPI.getData() as AllergieDetail;
    this.releaseAllergie(body.sequence);
  }

  private afterSaveVisibility() : void {
    this.handleDisplayButtons(true, true);
    this.allergieFormAPI.setDisabled(false);
    this.actionDeleteAPI.setText(MessageService.translate('tws.form.delete'))
  }

  private popNavigationToAllergy() : void {
    //applicationContext.redirect(APPLICATION_ROUTES.MY_CHART_ALLERGIES)
    applicationContext.popNavigationItem();
  }

  private handleDisplayButtons(showDelete: boolean, showSubmit: boolean, notChangeDelete?: boolean, notChangeSubmit?: boolean): void {
    if (!notChangeDelete) {
      this.actionDeleteAPI.setEnabled(showDelete);
    }
    if (!notChangeSubmit) {
      this.actionSubmitAPI.setEnabled(showSubmit);
    }
  }

  private validateDates(allergie: AllergieDetail): void {
    const now = moment();
    const momentDtStart = moment(allergie.startDate, 'YYYY-MM-DD');
    const momentDtEnd = moment(allergie.endDate, 'YYYY-MM-DD');

    if (allergie.startDate && momentDtStart.isAfter(now) || allergie.endDate && momentDtEnd.isAfter(now)) {
      showToastNotificationError(MessageService.translate('patient.my-chart.date-later-then-current-error'));
    }

    if (allergie.startDate && allergie.endDate && momentDtStart.isAfter(momentDtEnd)) {
      showToastNotificationError(MessageService.translate('patient.my-chart.start-after-end-error'));
    }
  }

  private async onChangeFieldSwap(
    requiredField: FieldAPI, notRequiredField: FieldAPI, validationField: boolean,
    enableField: InputAPI | AutocompleteAPI, notEnableField?: InputAPI | AutocompleteAPI): Promise<void> {
    const validator = { name: ValidatorsInUse.REQUIRED };
    let refreshField!: FieldAPI;
    if (!validationField ) {
      refreshField = requiredField;
      notRequiredField.removeValidator(ValidatorsInUse.REQUIRED);
      notRequiredField.setValue('');
    } else {
      refreshField = notRequiredField;
      requiredField.removeValidator(ValidatorsInUse.REQUIRED);
      if (notEnableField) {
        notEnableField.setEnabled(true);
      }
    }
    if(!refreshField.getValidators().find(item => item.name === validator.name)) {
      refreshField.addValidator(validator);
    }
    await requiredField.validate();
    enableField.setEnabled(validationField);

  }
}
